import { captureException } from '@sentry/nextjs';

import { LocationType, MimeTypes } from '@app/enums';
import { fetchPreviewDocument, isDateRangeValid } from '@app/helpers/cms.helpers';
import { FaqSection } from '@app/models';
import { CampaignListingEnriched, CmsApiResponse } from '@app/models/cms-api';
import { CmsServiceBase } from '@app/services/abstracts/cms-service.base';

export class CmsListingService extends CmsServiceBase {
    private static _instance: CmsListingService;

    public static get instance(): CmsListingService {
        if (!this._instance) {
            this._instance = new CmsListingService();
        }

        return this._instance;
    }

    async getCampaignListing(
        slug?: string,
        documentId?: string,
        preview = false,
    ): Promise<CampaignListingEnriched | null> {
        try {
            const populate = '*';

            if (preview && documentId) {
                return await fetchPreviewDocument<CampaignListingEnriched>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'campaign-listings',
                    documentId,
                    populate,
                    entityName: 'Campaign Listing',
                });
            }

            const params = this.buildStrapiParams({
                filters: {
                    slug: {
                        $eq: slug,
                    },
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                status: preview ? 'draft' : 'published',
                populate,
            });

            const response = await this.axiosInstance.get<
                CmsApiResponse<CampaignListingEnriched[]>
            >('/campaign-listings', {
                params,
            });
            const listingData = response.data.data?.[0];

            if (
                !listingData ||
                !isDateRangeValid(
                    listingData,
                    data => !!data.config && !!data.accommodationsIDs?.length,
                )
            ) {
                return null;
            }

            return listingData;
        } catch (error) {
            console.warn('Failed to fetch campaign listing:', error);
            return null;
        }
    }

    /**
     * Fetches geography description with content negotiation.
     */
    async getDescription(
        type: LocationType,
        id: string,
        options?: {
            serviceId?: number;
            categoryId?: number;
            accept?: MimeTypes.markdown | MimeTypes.html | MimeTypes.json;
        },
    ): Promise<string | null> {
        const url = `/geography/${type}/${id}/description`;
        try {
            const { serviceId, categoryId, accept = MimeTypes.json } = options || {};
            const params: Record<string, any> = {};

            if (serviceId !== undefined) {
                params.serviceId = serviceId;
            }

            if (categoryId !== undefined) {
                params.categoryId = categoryId;
            }

            const response = await this.axiosInstance.get(url, {
                params,
                headers: { Accept: accept },
            });

            // If Accept is application/json, expect { description: string }
            if (accept === 'application/json') {
                return response.data?.description || null;
            }
            // For text/markdown or text/html, response is the raw string
            if (typeof response.data === 'string') {
                return response.data;
            }
            return null;
        } catch (error) {
            captureException(error);

            return null;
        }
    }

    /**
     * Fetches geography FAQ with content negotiation.
     */
    async getFaq(
        type: LocationType,
        id: string,
        options?: {
            serviceId?: number;
            categoryId?: number;
            accept?: MimeTypes.markdown | MimeTypes.html | MimeTypes.json;
        },
    ): Promise<FaqSection | string | null> {
        const url = `/geography/${type}/${id}/faq`;
        try {
            const { serviceId, categoryId, accept = MimeTypes.json } = options || {};
            const params: Record<string, any> = {};

            if (serviceId !== undefined) {
                params.serviceId = serviceId;
            }

            if (categoryId !== undefined) {
                params.categoryId = categoryId;
            }

            const response = await this.axiosInstance.get(url, {
                params,
                headers: { Accept: accept },
            });

            if (accept === MimeTypes.json) {
                return (response.data as FaqSection) || null;
            }
            // For text/markdown or text/html, response is the raw string
            if (typeof response.data === 'string') {
                return response.data;
            }
            return null;
        } catch (error) {
            captureException(error);
            return null;
        }
    }
}
